<script setup lang="ts">
import { Chart } from 'highcharts-vue';
import DatapointDialog from './DatapointDialog.vue';
import {
  NumericDataPointDto,
  NumericDataSetDto,
  NumericEvaluationParameterDto,
} from '@/api/generator';
import { computed, ref, toRef } from 'vue';
import * as Highcharts from 'highcharts';
import { getSetupParameterName } from '@/views/tasks/utils';
import { useTranslation } from 'i18next-vue';
import { getDateTimeFormatted } from '@/components/formatters/utils';
import { getFormattingOptionsFromEvaluationParameter, toDisplayValue } from '@/utils/numberUtils';
import { highchartSymbols, useMergeWithChartDefaults } from './utils';

import {
  getEvaluationTypeString,
  getFullEvaluationTypeName,
} from '@/views/TrendsDisplayView/utils';
import StatisticsDialog from './StatisticsDialog.vue';
import { calculateDataPointsBands, bandColors, BandId } from '@/utils/bandLimits';
import { lightThemeColors } from '@/styles/vuetify';

interface NumericTrendChartProps {
  datasets: NumericDataSetDto[];
  showInJobContext?: boolean;
  startDate?: string;
  endDate?: string;
  markedEvaluationResultIds?: string[];
}

const props = defineProps<NumericTrendChartProps>();

const { t } = useTranslation();

function getUniqueNumericEvaluationParameter(evaluationParameter: NumericEvaluationParameterDto) {
  const val = `${evaluationParameter.dataTypeId}_${evaluationParameter.evaluationType}_${evaluationParameter.evaluationUnitAbbreviation}`;
  return val;
}

function createDataSetLimitSeries(targetSeriesId: string, dataSetCandidates: NumericDataSetDto[]) {
  const dataset = dataSetCandidates.find(
    (dataSetCandidate) => dataSetCandidate.id === targetSeriesId,
  );

  if (!dataset) {
    return [];
  }

  const { bands } = calculateDataPointsBands(dataset.data, 10);

  return Object.entries(bands)
    .map(([bandId, data]) => ({
      data,
      color: bandColors[bandId as BandId],
    }))
    .map((band) => {
      const newSeries: Highcharts.SeriesOptionsType = {
        // prevent the limits from showing up in the csv file
        includeInDataExport: false,
        yAxis: getUniqueNumericEvaluationParameter(dataset.evaluationParameter),
        enableMouseTracking: false,
        name: 'limits',
        type: 'arearange',
        lineWidth: 0,
        linkedTo: dataset.id,
        color: band.color,
        fillOpacity: 0.2,
        zIndex: -1,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        data: band.data,
      };

      return newSeries;
    });
}

function generateTooltip({
  dataset,
  data,
  seriesName,
}: {
  dataset: NumericDataSetDto;
  data: NumericDataPointDto;
  seriesName?: string;
}) {
  const { commentCount, lastComment } = data;

  const creationTime = getDateTimeFormatted({
    value: data.creationTime,
  });

  const creationString = `+ ${creationTime} | ${data.creatorUserFullName}`;

  // compute value
  const value = toDisplayValue(
    data.deviationValue,
    getFormattingOptionsFromEvaluationParameter(dataset.evaluationParameter),
  );

  // compute texts up front (prevents nested template literals)
  const valueLabel = t('Value');
  const latestCommentLabel = t('LatestComment');
  const resultLabel = t('Result_one');
  const evaluationText = t(`EvaluationStateEnum.${data.evaluationOutcome}`);

  // build comments block separately
  let commentsString = '';
  if (commentCount) {
    const additional =
      commentCount > 1
        ? ` (+ ${commentCount - 1} ${t('Comments', { count: commentCount - 1 })})`
        : '';

    commentsString = `<br /><br />${latestCommentLabel}:<br />${lastComment}${additional}`;
  }

  // final template literal stays flat and simple
  return `
    <strong>${seriesName ?? ''}</strong><br />
    ${creationString}<br /><br />
    ${valueLabel}: <strong>${value}</strong><br />
    ${resultLabel}: <strong>${evaluationText}</strong>
    ${commentsString}
  `.trim();
}

const options = computed<Highcharts.Options>(() => ({
  exporting: {
    enabled: false,
  },
  chart: {
    height: 500,
  },
  tooltip: {
    formatter: function () {
      return this.custom?.tooltip || '';
    },
  },
  yAxis: props.datasets.reduce((prev, currDataset) => {
    if (
      prev.some(
        (prevDataset) =>
          prevDataset.id === getUniqueNumericEvaluationParameter(currDataset.evaluationParameter),
      )
    ) {
      return prev;
    } else {
      const yAxisId = getUniqueNumericEvaluationParameter(currDataset.evaluationParameter);
      const hasVisibleSeries = props.datasets.some(
        (dataset) =>
          visibleSeries.value.includes(dataset.id) &&
          getUniqueNumericEvaluationParameter(dataset.evaluationParameter) === yAxisId,
      );

      return [
        ...prev,
        {
          startOnTick: false,
          endOnTick: false,
          id: yAxisId,
          title: {
            text: hasVisibleSeries
              ? `${getSetupParameterName(currDataset.evaluationParameter, { ignoreGeometry: true })} - ${getEvaluationTypeString(currDataset.evaluationParameter)}`
              : '',
          },
          opposite: Boolean(prev.length % 2),
          labels: {
            formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
              return toDisplayValue(
                this.value as number,
                getFormattingOptionsFromEvaluationParameter(currDataset.evaluationParameter),
              );
            },
          },
        },
      ];
    }
  }, [] as Highcharts.YAxisOptions[]),

  // this is where the points are in
  series: [
    ...props.datasets.map<Highcharts.SeriesOptionsType>((dataset, index) => {
      const seriesName = getFullEvaluationTypeName(dataset.evaluationParameter);
      return {
        id: dataset.id, // important: may not be the same as yAxis (below)
        yAxis: getUniqueNumericEvaluationParameter(dataset.evaluationParameter),
        type: 'line',
        lineWidth: 2,
        name: seriesName,
        data: dataset.data.map<Highcharts.PointOptionsObject>((data) => {
          const dataPoint = {
            id: data.id,
            x: data.creationTime,
            y: data.deviationValue,
            // Keep this `custom`-prop as lightweight as possible
            custom: {
              tooltip: generateTooltip({ data, dataset, seriesName }),
              commentCount: data.commentCount,
            },
            events: {
              click: () => onDatapointClick(data),
            },
            marker: {
              enabled: true, // force (even when overlapping, because to close)
              ...(props.markedEvaluationResultIds?.includes(data.id)
                ? {
                    fillColor: lightThemeColors['primary'],
                    symbol: 'triangle-down',
                    radius: 8,
                  }
                : {}),
            },
          };

          return dataPoint;
        }),
        marker: {
          symbol: highchartSymbols[index % highchartSymbols.length].key,
        },
      };
    }),
    // show limits immediately when there is only one dataset
    ...(visibleSeries.value?.length === 1
      ? createDataSetLimitSeries(visibleSeries.value[0], props.datasets)
      : []),
  ],
  plotOptions: {
    series: {
      dataLabels: {
        x: -10,
        y: 3,
      },
    },
  },
}));

const {
  chartComponent,
  onDatapointClick,
  zooming,
  zoomOut,
  selectedDatapointId,
  targetOptions,
  visibleSeries,
} = useMergeWithChartDefaults({
  options,
  showInJobContext: toRef(() => props.showInJobContext),
  startDate: toRef(() => props.startDate),
  endDate: toRef(() => props.endDate),
});

const visibleDataSets = computed(() =>
  props.datasets.filter((dataSetCandidate) => visibleSeries.value.includes(dataSetCandidate.id)),
);

const showStatistics = ref(false);
</script>

<template>
  <v-sheet v-if="datasets?.length" class="pa-6 position-relative">
    <Chart ref="chartComponent" :options="targetOptions"></Chart>
    <v-btn v-if="zooming" class="mr-4" @click="zoomOut">{{ $t('ResetZoom') }}</v-btn>
    <v-btn v-if="!showInJobContext" @click="showStatistics = true">{{
      $t('ShowStatisticsBtn')
    }}</v-btn>

    <DatapointDialog v-if="!showInJobContext" v-model:datapointId="selectedDatapointId" />
    <StatisticsDialog v-model="showStatistics" :dataSets="visibleDataSets" />
  </v-sheet>
</template>
